import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  getAnnouncements,
  deleteAnnouncement,
} from "@/services/firebase/announcement"
import { Announcement } from "@/types/announcement"
import ConfirmDialog from "@/components/common/ConfirmDialog"
import ActionMenu from "@/components/common/ActionMenu"
import { useDebounce } from "@/hooks/useDebounce"
import { AdminPage, AdminPageHeader, AdminSearchInput, AdminLoadingSkeleton, AdminErrorAlert } from "@/components/admin-ui"
import { AdminDataTable } from "@/components/admin-ui/admin-data-table"
import type { AdminTableColumn } from "@/components/admin-ui/admin-data-table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

const columns: AdminTableColumn[] = [
  { key: "title", label: "標題" },
  { key: "status", label: "狀態" },
  { key: "displayMode", label: "顯示模式", hideBelow: "md" },
  { key: "targetAudience", label: "目標受眾", hideBelow: "lg" },
  { key: "startAt", label: "開始時間", hideBelow: "lg" },
  { key: "endAt", label: "結束時間", hideBelow: "lg" },
  { key: "createdAt", label: "建立時間", hideBelow: "md" },
  { key: "actions", label: "操作", width: "w-16" },
]

function getDisplayModeLabel(mode?: string): string {
  switch (mode) {
    case "once": return "一次性"
    case "everyTime": return "每次顯示"
    case "daily": return "每日顯示"
    default: return "一次性"
  }
}

export default function AnnouncementListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "inactive">("all")
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean
    id: string | null
    title: string
  }>({ isOpen: false, id: null, title: "" })

  const { data: announcements, isLoading, error } = useQuery({
    queryKey: ["announcements"],
    queryFn: () => getAnnouncements(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] })
      setDeleteConfirm({ isOpen: false, id: null, title: "" })
      setDeleteError(null)
    },
    onError: (error) => {
      console.error("刪除公告失敗:", error)
      setDeleteError(error instanceof Error ? error.message : "未知錯誤")
      setDeleteConfirm({ isOpen: false, id: null, title: "" })
    },
  })

  const filteredAnnouncements = useMemo(() => {
    if (!announcements || announcements.length === 0) return []

    return announcements.filter((announcement) => {
      const searchLower = debouncedSearchTerm.toLowerCase()
      const matchesSearch =
        !searchLower ||
        announcement.title.toLowerCase().includes(searchLower) ||
        announcement.content.toLowerCase().includes(searchLower)

      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && announcement.isActive) ||
        (filterStatus === "inactive" && !announcement.isActive)

      return matchesSearch && matchesStatus
    })
  }, [announcements, debouncedSearchTerm, filterStatus])

  const handleDelete = (id: string, title: string) => {
    setDeleteConfirm({ isOpen: true, id, title })
  }

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id)
    }
  }

  if (isLoading) return <AdminPage><AdminLoadingSkeleton /></AdminPage>

  if (error) {
    return (
      <AdminPage>
        <AdminErrorAlert message={`載入公告時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`} />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      {deleteError && (
        <AdminErrorAlert message={`刪除公告失敗：${deleteError}`} onDismiss={() => setDeleteError(null)} />
      )}

      <AdminPageHeader
        title="公告管理"
        createHref="/announcements/new"
        createLabel="新增公告"
        actions={
          <>
            <AdminSearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="搜尋標題或內容..."
            />
            <div className="flex gap-1">
              {(["all", "active", "inactive"] as const).map((status) => (
                <Button
                  key={status}
                  variant={filterStatus === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                >
                  {status === "all" ? "全部" : status === "active" ? "啟用中" : "已停用"}
                </Button>
              ))}
            </div>
          </>
        }
      />

      <AdminDataTable
        columns={columns}
        data={filteredAnnouncements}
        getRowId={(item) => item.id}
        emptyTitle={searchTerm || filterStatus !== "all" ? "沒有符合條件的公告" : "尚無公告資料"}
        emptyActionHref={!searchTerm && filterStatus === "all" ? "/announcements/new" : undefined}
        emptyActionLabel="新增第一個公告"
        renderCell={(item: Announcement, col) => {
          switch (col.key) {
            case "title":
              return (
                <div>
                  <div className="text-sm font-medium text-foreground">{item.title}</div>
                  {item.content && (
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {item.content.length > 50 ? `${item.content.substring(0, 50)}...` : item.content}
                    </div>
                  )}
                </div>
              )
            case "status":
              return item.isActive ? (
                <Badge variant="success">啟用中</Badge>
              ) : (
                <Badge variant="secondary">已停用</Badge>
              )
            case "displayMode":
              return <span className="text-sm text-muted-foreground">{getDisplayModeLabel(item.displayMode)}</span>
            case "targetAudience":
              return (
                <span className="text-sm text-muted-foreground">
                  {item.targetAudience && item.targetAudience.length > 0 ? item.targetAudience.join(", ") : "全部用戶"}
                </span>
              )
            case "startAt":
              return <span className="text-sm text-muted-foreground">{item.startAt ? new Date(item.startAt).toLocaleString("zh-TW") : "-"}</span>
            case "endAt":
              return <span className="text-sm text-muted-foreground">{item.endAt ? new Date(item.endAt).toLocaleString("zh-TW") : "-"}</span>
            case "createdAt":
              return <span className="text-sm text-muted-foreground">{item.createdAt ? new Date(item.createdAt).toLocaleString("zh-TW") : "-"}</span>
            case "actions":
              return (
                <ActionMenu
                  actions={[
                    { label: "編輯", onClick: () => navigate(`/announcements/${item.id}/edit`) },
                    { label: "刪除", onClick: () => handleDelete(item.id, item.title), disabled: deleteMutation.isPending, variant: "danger" },
                  ]}
                />
              )
            default:
              return null
          }
        }}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除公告「${deleteConfirm.title}」嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, title: "" })}
      />
    </AdminPage>
  )
}
